import {objectFromEntries} from '@benchling/js-utils';

import {assert} from 'Asserts';

export function mergeIdObjectArrays<T extends {id: string}>(base: Array<T>, toMerge: Array<T>): Array<T> {
  return Object.values({
    ...objectFromEntries(base.map((p) => [p.id, p])),
    ...objectFromEntries(toMerge.map((p) => [p.id, p])),
  });
}

/**
 * Merges two arrays of objects with an `id` property and sorts the result by their position in the arrays.
 *
 * The resulting array is sorted such that
 * - If an element is in `base`, it is sorted by its position in `base`
 * - If an element is not in `base`, it is put at the end of the array and sorted by its position in `toMerge`
 *
 * @param base An array
 * @param toMerge An array to merge into `base`
 * @returns A new array that is the result of merging `toMerge` into `base` and sorting the result by their
 * original position in `base`. If an element is not in `base`, it is sorted by its position in `toMerge`.
 */
export default function mergeAndSortIdObjectArrays<T extends {id: string}>(
  base: Array<T>,
  toMerge: Array<T>
): Array<T> {
  const merged = mergeIdObjectArrays(base, toMerge);
  const getPos = (el: T): number => {
    const baseIdx = base.findIndex((b) => b.id === el.id);
    const toMergeIdx = baseIdx === -1 ? toMerge.findIndex((it) => it.id === el.id) : -1;

    assert(baseIdx >= 0 || toMergeIdx >= 0, 'Expected element to be in one of the arrays');
    return baseIdx >= 0 ? baseIdx : base.length + toMergeIdx;
  };
  return merged.sort((a, b) => getPos(a) - getPos(b));
}
